import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Download, FileText, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import BookingModal from '@/components/BookingModal';
import EmailCaptureModal from '@/components/EmailCaptureModal';
import MortgageCalculator from '@/components/MortgageCalculator';
import { SmsOptInCheckbox } from '@/components/SmsConsentDisclosure';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const BuyHome = () => {
  const [showBooking, setShowBooking] = useState(false);
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const [buyerForm, setBuyerForm] = useState({
    name: '',
    email: '',
    phone: '',
    location_preference: '',
    price_range: '',
    bedrooms: '',
    property_type: '',
    timeline: '',
    pre_approved: false,
    additional_info: '',
    sms_opt_in: false,
  });

  const handleBuyerFormChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setBuyerForm({ ...buyerForm, [e.target.name]: value });
  };

  const handleBuyerSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/buyer-inquiry', buyerForm);
      toast.success('Your inquiry has been submitted! We\'ll contact you soon.');
      setBuyerForm({
        name: '',
        email: '',
        phone: '',
        location_preference: '',
        price_range: '',
        bedrooms: '',
        property_type: '',
        timeline: '',
        pre_approved: false,
        additional_info: ''
      });
    } catch (error) {
      toast.error('Failed to submit inquiry. Please try again.');
    }
  };

  return (
    <div className="min-h-screen" data-testid="buy-home-page">
      {/* Hero Banner */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50 z-10"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_agent-platform-28/artifacts/4dsj9zgd_The%20Heart%20of%20Bespoke%283%29.png)',
          }}
        ></div>
        <div className="relative z-20 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl font-bold mb-4 text-white" data-testid="buy-heading">Find Your Dream Home</h1>
          <p className="text-xl text-white">Expert guidance every step of the way</p>
        </div>
      </section>

      {/* Steps to Buying - RIGHT AFTER HERO */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12">Steps to Buying with Bespoke</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#BCA480] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">1</div>
              <h3 className="text-xl font-bold mb-2">Consultation</h3>
              <p className="text-gray-600">Discuss your needs and goals with our expert agents</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#BCA480] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">2</div>
              <h3 className="text-xl font-bold mb-2">Property Search</h3>
              <p className="text-gray-600">We find properties that match your criteria</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#BCA480] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">3</div>
              <h3 className="text-xl font-bold mb-2">Viewing & Offer</h3>
              <p className="text-gray-600">Tour homes and make competitive offers</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#BCA480] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">4</div>
              <h3 className="text-xl font-bold mb-2">Closing</h3>
              <p className="text-gray-600">Navigate the closing process smoothly</p>
            </div>
          </div>
        </div>
      </section>

      {/* Buyer Intake Form */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-3xl font-bold mb-6" data-testid="buyer-form-heading">Start Your Home Search</h2>
            <p className="text-gray-600 mb-8">Tell us what you're looking for, and we'll match you with the perfect property.</p>
            
            <form onSubmit={handleBuyerSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={buyerForm.name}
                    onChange={handleBuyerFormChange}
                    required
                    data-testid="buyer-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={buyerForm.email}
                    onChange={handleBuyerFormChange}
                    required
                    data-testid="buyer-email-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={buyerForm.phone}
                    onChange={handleBuyerFormChange}
                    required
                    data-testid="buyer-phone-input"
                  />
                  <SmsOptInCheckbox
                    checked={buyerForm.sms_opt_in}
                    onChange={(v) => setBuyerForm({ ...buyerForm, sms_opt_in: v })}
                  />
                </div>
                <div>
                  <Label htmlFor="location_preference">Preferred Location *</Label>
                  <Input
                    id="location_preference"
                    name="location_preference"
                    placeholder="e.g., Memphis, TN"
                    value={buyerForm.location_preference}
                    onChange={handleBuyerFormChange}
                    required
                    data-testid="buyer-location-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="price_range">Price Range *</Label>
                  <Select name="price_range" value={buyerForm.price_range} onValueChange={(value) => setBuyerForm({...buyerForm, price_range: value})}>
                    <SelectTrigger data-testid="buyer-price-select">
                      <SelectValue placeholder="Select price range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0-150000">Under $150,000</SelectItem>
                      <SelectItem value="150000-300000">$150,000 - $300,000</SelectItem>
                      <SelectItem value="300000-500000">$300,000 - $500,000</SelectItem>
                      <SelectItem value="500000-750000">$500,000 - $750,000</SelectItem>
                      <SelectItem value="750000+">$750,000+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="bedrooms">Minimum Bedrooms</Label>
                  <Input
                    id="bedrooms"
                    name="bedrooms"
                    type="number"
                    min="1"
                    value={buyerForm.bedrooms}
                    onChange={handleBuyerFormChange}
                    data-testid="buyer-bedrooms-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="property_type">Property Type *</Label>
                  <Select name="property_type" value={buyerForm.property_type} onValueChange={(value) => setBuyerForm({...buyerForm, property_type: value})}>
                    <SelectTrigger data-testid="buyer-type-select">
                      <SelectValue placeholder="Select property type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="house">House</SelectItem>
                      <SelectItem value="condo">Condo</SelectItem>
                      <SelectItem value="townhouse">Townhouse</SelectItem>
                      <SelectItem value="land">Land</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="timeline">Timeline *</Label>
                  <Select name="timeline" value={buyerForm.timeline} onValueChange={(value) => setBuyerForm({...buyerForm, timeline: value})}>
                    <SelectTrigger data-testid="buyer-timeline-select">
                      <SelectValue placeholder="When are you looking to buy?" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="immediately">Immediately</SelectItem>
                      <SelectItem value="1-3-months">1-3 months</SelectItem>
                      <SelectItem value="3-6-months">3-6 months</SelectItem>
                      <SelectItem value="6-12-months">6-12 months</SelectItem>
                      <SelectItem value="just-looking">Just looking</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="additional_info">Additional Information</Label>
                <Textarea
                  id="additional_info"
                  name="additional_info"
                  value={buyerForm.additional_info}
                  onChange={handleBuyerFormChange}
                  rows={4}
                  placeholder="Any specific requirements or preferences?"
                  data-testid="buyer-additional-input"
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="pre_approved"
                  name="pre_approved"
                  checked={buyerForm.pre_approved}
                  onChange={handleBuyerFormChange}
                  className="h-4 w-4"
                  data-testid="buyer-preapproved-checkbox"
                />
                <Label htmlFor="pre_approved" className="font-normal">I am pre-approved for a mortgage</Label>
              </div>

              <Button 
                type="submit" 
                size="lg" 
                className="w-full bg-[#BCA480] hover:bg-[#a89470]"
                data-testid="buyer-submit-btn"
              >
                Submit Inquiry
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Complimentary Buyer's Consultation */}
      <section id="buyer-process" className="py-16 bg-gradient-to-br from-[#BCA480]/10 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-3xl font-bold mb-6 text-center">Complimentary Buyer's Consultation</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              At your complimentary consultation, we'll review your goals, financing options, and the home-buying process.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Bespoke Realty follows state laws regarding Buyer Representation — we'll explain agency relationships and how we protect your best interests before you begin your search.
            </p>
            <div className="bg-[#BCA480]/10 p-6 rounded-lg mb-6">
              <h3 className="text-xl font-bold mb-3">Download Our Complimentary Home Buyer Guide</h3>
              <p className="text-gray-700 mb-4">Get expert insights, checklists, and tips to navigate your home buying journey with confidence.</p>
              <Button 
                className="bg-[#BCA480] hover:bg-[#a89470]"
                onClick={() => setShowEmailCapture(true)}
              >
                <Download className="h-4 w-4 mr-2" />
                Download Buyer's Guide
              </Button>
            </div>
            <Button 
              onClick={() => setShowBooking(true)}
              size="lg"
              className="w-full bg-[#434345] hover:bg-[#000000]"
            >
              Schedule Your Free Consultation
            </Button>
          </div>
        </div>
      </section>

      {/* Mortgage Calculator */}
      <MortgageCalculator />

      {/* Homes We've Sold */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#434345] mb-4">Homes We've Sold</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Take a look at some of the beautiful homes we've helped our clients purchase in the Memphis metro area.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Sold Home 1 */}
            <div className="group relative overflow-hidden rounded-xl shadow-lg">
              <img 
                src="https://customer-assets.emergentagent.com/job_eduhub-78/artifacts/e3ho7myy_OliveBranchRealEstate.jpg"
                alt="Sold home in Olive Branch, MS"
                className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="flex items-center gap-2 text-white">
                  <MapPin className="h-4 w-4 text-[#BCA480]" />
                  <span className="font-medium">Olive Branch, MS</span>
                </div>
                <span className="inline-block mt-2 px-3 py-1 bg-[#BCA480] text-white text-sm font-semibold rounded-full">SOLD</span>
              </div>
            </div>

            {/* Sold Home 2 */}
            <div className="group relative overflow-hidden rounded-xl shadow-lg">
              <img 
                src="https://customer-assets.emergentagent.com/job_eduhub-78/artifacts/wni4ozca_Southavenrealestate.jpg"
                alt="Sold home in Southaven, MS"
                className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="flex items-center gap-2 text-white">
                  <MapPin className="h-4 w-4 text-[#BCA480]" />
                  <span className="font-medium">Southaven, MS</span>
                </div>
                <span className="inline-block mt-2 px-3 py-1 bg-[#BCA480] text-white text-sm font-semibold rounded-full">SOLD</span>
              </div>
            </div>

            {/* Sold Home 3 */}
            <div className="group relative overflow-hidden rounded-xl shadow-lg">
              <img 
                src="https://customer-assets.emergentagent.com/job_eduhub-78/artifacts/fksdojn4_nesbitrealestate.jpg"
                alt="Sold home in Nesbit, MS"
                className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="flex items-center gap-2 text-white">
                  <MapPin className="h-4 w-4 text-[#BCA480]" />
                  <span className="font-medium">Nesbit, MS</span>
                </div>
                <span className="inline-block mt-2 px-3 py-1 bg-[#BCA480] text-white text-sm font-semibold rounded-full">SOLD</span>
              </div>
            </div>

            {/* Sold Home 4 */}
            <div className="group relative overflow-hidden rounded-xl shadow-lg">
              <img 
                src="https://customer-assets.emergentagent.com/job_eduhub-78/artifacts/82939t4o_southavenrealestate1.jpg"
                alt="Sold home in Southaven, MS"
                className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="flex items-center gap-2 text-white">
                  <MapPin className="h-4 w-4 text-[#BCA480]" />
                  <span className="font-medium">Southaven, MS</span>
                </div>
                <span className="inline-block mt-2 px-3 py-1 bg-[#BCA480] text-white text-sm font-semibold rounded-full">SOLD</span>
              </div>
            </div>

            {/* Sold Home 5 */}
            <div className="group relative overflow-hidden rounded-xl shadow-lg">
              <img 
                src="https://customer-assets.emergentagent.com/job_eduhub-78/artifacts/wfc97jt8_OliveBranchRealestate1.webp"
                alt="Sold home in Olive Branch, MS"
                className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="flex items-center gap-2 text-white">
                  <MapPin className="h-4 w-4 text-[#BCA480]" />
                  <span className="font-medium">Olive Branch, MS</span>
                </div>
                <span className="inline-block mt-2 px-3 py-1 bg-[#BCA480] text-white text-sm font-semibold rounded-full">SOLD</span>
              </div>
            </div>

            {/* Sold Home 6 */}
            <div className="group relative overflow-hidden rounded-xl shadow-lg">
              <img 
                src="https://customer-assets.emergentagent.com/job_eduhub-78/artifacts/sczmqt04_OliveBranchRealEstate2.webp"
                alt="Sold home in Olive Branch, MS"
                className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="flex items-center gap-2 text-white">
                  <MapPin className="h-4 w-4 text-[#BCA480]" />
                  <span className="font-medium">Olive Branch, MS</span>
                </div>
                <span className="inline-block mt-2 px-3 py-1 bg-[#BCA480] text-white text-sm font-semibold rounded-full">SOLD</span>
              </div>
            </div>

            {/* Sold Home 7 */}
            <div className="group relative overflow-hidden rounded-xl shadow-lg">
              <img 
                src="https://customer-assets.emergentagent.com/job_eduhub-78/artifacts/3vdk96bk_nesbitrealestate2.webp"
                alt="Sold home in Nesbit, MS"
                className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="flex items-center gap-2 text-white">
                  <MapPin className="h-4 w-4 text-[#BCA480]" />
                  <span className="font-medium">Nesbit, MS</span>
                </div>
                <span className="inline-block mt-2 px-3 py-1 bg-[#BCA480] text-white text-sm font-semibold rounded-full">SOLD</span>
              </div>
            </div>

            {/* Sold Home 8 */}
            <div className="group relative overflow-hidden rounded-xl shadow-lg">
              <img 
                src="https://customer-assets.emergentagent.com/job_eduhub-78/artifacts/zf8w38t5_ByhaliaRealestate1.webp"
                alt="Sold home in Byhalia, MS"
                className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="flex items-center gap-2 text-white">
                  <MapPin className="h-4 w-4 text-[#BCA480]" />
                  <span className="font-medium">Byhalia, MS</span>
                </div>
                <span className="inline-block mt-2 px-3 py-1 bg-[#BCA480] text-white text-sm font-semibold rounded-full">SOLD</span>
              </div>
            </div>

            {/* Sold Home 9 */}
            <div className="group relative overflow-hidden rounded-xl shadow-lg">
              <img 
                src="https://customer-assets.emergentagent.com/job_eduhub-78/artifacts/gdce7r0b_OliveBranchRealestate3.webp"
                alt="Sold home in Olive Branch, MS"
                className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="flex items-center gap-2 text-white">
                  <MapPin className="h-4 w-4 text-[#BCA480]" />
                  <span className="font-medium">Olive Branch, MS</span>
                </div>
                <span className="inline-block mt-2 px-3 py-1 bg-[#BCA480] text-white text-sm font-semibold rounded-full">SOLD</span>
              </div>
            </div>

            {/* CTA Card */}
            <div className="flex flex-col items-center justify-center bg-gradient-to-br from-[#434345] to-[#5a5a5c] rounded-xl p-8 text-white text-center">
              <h3 className="text-2xl font-bold mb-3">Your Home Could Be Next</h3>
              <p className="text-white/80 mb-6">Let us help you find your dream home in the Memphis metro area.</p>
              <Button 
                onClick={() => setShowBooking(true)}
                className="bg-[#BCA480] hover:bg-[#a89470] text-white"
              >
                Start Your Search
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ for Buyers */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="faq-1" className="border rounded-lg px-6 bg-gray-50">
              <AccordionTrigger className="text-lg font-semibold hover:text-[#BCA480] hover:no-underline">
                Do I need pre-approval before viewing homes?
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 pt-2">
                As of the new real estate laws effective August 17th, buyers must complete a consultation before viewing homes. Pre-approval is still strongly recommended because it clarifies your budget and strengthens your position, but the consultation is required before touring any properties. We can connect you with trusted lenders in Mississippi, Tennessee, and Arkansas.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-2" className="border rounded-lg px-6 bg-gray-50">
              <AccordionTrigger className="text-lg font-semibold hover:text-[#BCA480] hover:no-underline">
                Who pays the buyer's agent commission?
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 pt-2">
                Commission is now negotiated between the buyer and their agent, according to the new federal rules. Buyers may pay their agent directly, have the seller contribute, or explore lender-approved financing options. We explain all of this during your Buyer Consultation so you understand your choices clearly.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-3" className="border rounded-lg px-6 bg-gray-50">
              <AccordionTrigger className="text-lg font-semibold hover:text-[#BCA480] hover:no-underline">
                What should I expect during inspections and appraisals?
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 pt-2">
                A professional home inspector will evaluate the property's structure, systems (plumbing, electrical, HVAC), roof, and foundation. We recommend attending the inspection to ask questions and understand any issues. The appraisal determines the property's market value for your lender. Based on inspection findings, we can negotiate repairs or credits with the seller before closing.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-4" className="border rounded-lg px-6 bg-gray-50">
              <AccordionTrigger className="text-lg font-semibold hover:text-[#BCA480] hover:no-underline">
                How long does closing take?
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 pt-2">
                It depends. In traditional financing, many closings occur within 21–30 days. When using government-backed loan programs, allow up to 45 days. Your lender, title company, and the specifics of the property all impact the timeline.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
          <div className="text-center mt-8">
            <Link to="/faq" className="inline-flex items-center text-[#BCA480] hover:text-[#a89470] font-semibold">
              View All FAQs →
            </Link>
          </div>
        </div>
      </section>

      {/* Property Search CTA - Link to Real MLS */}
      <section className="py-16 bg-[#BCA480] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Find Your Dream Home?</h2>
          <p className="text-xl mb-8">Browse live MLS listings in Memphis, Germantown, Collierville, and Northwest Mississippi.</p>
          <Link to="/mls">
            <Button 
              size="lg" 
              className="bg-white text-[#434345] hover:bg-gray-100 px-8"
              data-testid="browse-properties-btn"
            >
              Browse Available Properties
            </Button>
          </Link>
        </div>
      </section>

      <BookingModal isOpen={showBooking} onClose={() => setShowBooking(false)} bookingType="property_viewing" />
      <EmailCaptureModal 
        isOpen={showEmailCapture} 
        onClose={() => setShowEmailCapture(false)}
        downloadUrl="https://customer-assets.emergentagent.com/job_agent-platform-28/artifacts/fd4uhooq_NEW%20NAR%20HOME%20BUYER%20GUIDE-3.pdf"
        downloadName="Bespoke-Home-Buyer-Guide.pdf"
        resourceTitle="Home Buyer's Guide"
      />
    </div>
  );
};

export default BuyHome;
